import { Plugin, Command, ButtonView } from 'ckeditor5'

/**
 * Custom Alert Block Plugin for CKEditor 5
 */

// Alert types - only colors differ, structure is identical
const ALERT_TYPES = {
  info: {
    label: 'Info',
    class: 'alert-block--info',
    bgGradient: ['#dbeafe', '#bfdbfe'],
    borderColor: '#3b82f6',
    titleColor: '#1e40af',
    textColor: '#1e3a8a',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>'
  },
  warning: {
    label: 'Warning',
    class: 'alert-block--warning',
    bgGradient: ['#fef3c7', '#fde68a'],
    borderColor: '#f59e0b',
    titleColor: '#92400e',
    textColor: '#78350f',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>'
  },
  danger: {
    label: 'Danger',
    class: 'alert-block--danger',
    bgGradient: ['#fee2e2', '#fecaca'],
    borderColor: '#ef4444',
    titleColor: '#991b1b',
    textColor: '#7f1d1d',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>'
  },
  success: {
    label: 'Success',
    class: 'alert-block--success',
    bgGradient: ['#dcfce7', '#bbf7d0'],
    borderColor: '#22c55e',
    titleColor: '#166534',
    textColor: '#14532d',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>'
  }
}

const TOOLBAR_ICON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>'

class AlertBlockCommand extends Command {
  constructor(editor, alertType) {
    super(editor)
    this.alertType = alertType
  }

  refresh() {
    const model = this.editor.model
    const selection = model.document.selection
    const firstBlock = selection.getSelectedBlocks().next().value
    const alertBlock = this._findAlertBlockAncestor(firstBlock)
    this.isEnabled = !!firstBlock
    this.value = alertBlock && alertBlock.getAttribute('alertType') === this.alertType
  }

  execute() {
    const model = this.editor.model
    const selection = model.document.selection
    const blocks = Array.from(selection.getSelectedBlocks())
    if (!blocks.length) return

    const firstBlock = blocks[0]
    const alertBlock = this._findAlertBlockAncestor(firstBlock)

    model.change(writer => {
      if (alertBlock) {
        if (alertBlock.getAttribute('alertType') === this.alertType) {
          this._unwrapAlertBlock(writer, alertBlock)
        } else {
          writer.setAttribute('alertType', this.alertType, alertBlock)
        }
      } else {
        this._insertAlertBlock(writer, firstBlock)
      }
    })
  }

  _findAlertBlockAncestor(element) {
    if (!element) return null
    if (element.is('element', 'alertBlock')) return element
    return element.parent ? this._findAlertBlockAncestor(element.parent) : null
  }

  _unwrapAlertBlock(writer, alertBlock) {
    const parent = alertBlock.parent
    const index = alertBlock.index
    const children = Array.from(alertBlock.getChildren())
    for (let i = children.length - 1; i >= 0; i--) {
      writer.move(writer.createRangeOn(children[i]), writer.createPositionAt(parent, index))
    }
    writer.remove(alertBlock)
  }

  _insertAlertBlock(writer, currentBlock) {
    const alertBlock = writer.createElement('alertBlock', { alertType: this.alertType })

    const heading = writer.createElement('heading3')
    writer.insertText('Title goes here', heading)

    const paragraph = writer.createElement('paragraph')
    writer.insertText('Text goes here', paragraph)

    writer.insert(heading, alertBlock, 0)
    writer.insert(paragraph, alertBlock, 1)
    writer.insert(alertBlock, writer.createPositionAfter(currentBlock))
    writer.setSelection(heading, 'in')
  }
}

class AlertBlockEditing extends Plugin {
  static get pluginName() {
    return 'AlertBlockEditing'
  }

  init() {
    this._defineSchema()
    this._defineConverters()
    this._defineCommands()
    this._setupDataProcessor()
  }

  _defineSchema() {
    const schema = this.editor.model.schema
    schema.register('alertBlock', {
      allowWhere: '$block',
      allowContentOf: '$root',
      allowAttributes: ['alertType'],
      isLimit: true
    })
    schema.extend('$block', { allowIn: 'alertBlock' })
  }

  _defineConverters() {
    const conversion = this.editor.conversion

    conversion.for('upcast').elementToElement({
      view: { name: 'div', classes: 'alert-block' },
      model: (viewElement, { writer }) => {
        let alertType = 'info'
        for (const [type, config] of Object.entries(ALERT_TYPES)) {
          if (viewElement.hasClass(config.class)) {
            alertType = type
            break
          }
        }
        return writer.createElement('alertBlock', { alertType })
      }
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'alertBlock',
      view: (modelElement, { writer }) => {
        const alertType = modelElement.getAttribute('alertType') || 'info'
        const config = ALERT_TYPES[alertType] || ALERT_TYPES.info
        return writer.createContainerElement('div', {
          class: `alert-block ${config.class}`,
          style: `background: linear-gradient(135deg, ${config.bgGradient[0]} 0%, ${config.bgGradient[1]} 100%); border-left: 4px solid ${config.borderColor}; padding: 20px 24px; border-radius: 8px; margin: 24px 0;`
        })
      }
    })

    conversion.for('editingDowncast').add(dispatcher => {
      dispatcher.on('attribute:alertType:alertBlock', (evt, data, conversionApi) => {
        const viewElement = conversionApi.mapper.toViewElement(data.item)
        if (!viewElement) return

        const newType = data.attributeNewValue || 'info'
        const config = ALERT_TYPES[newType] || ALERT_TYPES.info

        for (const type of Object.keys(ALERT_TYPES)) {
          if (viewElement.hasClass(ALERT_TYPES[type].class)) {
            conversionApi.writer.removeClass(ALERT_TYPES[type].class, viewElement)
          }
        }
        conversionApi.writer.addClass(config.class, viewElement)
        conversionApi.writer.setAttribute('style', `background: linear-gradient(135deg, ${config.bgGradient[0]} 0%, ${config.bgGradient[1]} 100%); border-left: 4px solid ${config.borderColor}; padding: 20px 24px; border-radius: 8px; margin: 24px 0;`, viewElement)
      })
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'alertBlock',
      view: (modelElement, { writer }) => {
        const alertType = modelElement.getAttribute('alertType') || 'info'
        const config = ALERT_TYPES[alertType] || ALERT_TYPES.info
        return writer.createContainerElement('div', {
          class: `alert-block ${config.class}`,
          'data-alert-type': alertType
        })
      }
    })
  }

  _defineCommands() {
    for (const type of Object.keys(ALERT_TYPES)) {
      this.editor.commands.add(`alertBlock:${type}`, new AlertBlockCommand(this.editor, type))
    }
  }

  _setupDataProcessor() {
    const editor = this.editor
    const originalGetData = editor.getData.bind(editor)

    editor.getData = (options) => {
      let html = originalGetData(options)

      const parser = new DOMParser()
      const doc = parser.parseFromString(`<div id="wrapper">${html}</div>`, 'text/html')
      const alertBlocks = doc.querySelectorAll('.alert-block[data-alert-type]')

      alertBlocks.forEach(block => {
        const type = block.getAttribute('data-alert-type')
        const config = ALERT_TYPES[type] || ALERT_TYPES.info

        // Container style - exact match to example
        block.setAttribute('style', `background: linear-gradient(135deg, ${config.bgGradient[0]} 0%, ${config.bgGradient[1]} 100%); border-left: 4px solid ${config.borderColor}; padding: 20px 24px; border-radius: 8px; margin: 24px 0;`)
        block.removeAttribute('data-alert-type')

        // Style heading with icon - exact match to example
        const heading = block.querySelector('h1, h2, h3, h4')
        if (heading) {
          const headingText = heading.textContent
          heading.setAttribute('style', `margin: 0 0 12px 0; color: ${config.titleColor}; font-size: 18px; font-weight: 700; display: flex; align-items: center; gap: 8px;`)
          heading.innerHTML = `${config.icon}\n        ${headingText}`
        }

        // Style paragraphs - exact match to example
        const paragraphs = block.querySelectorAll('p')
        paragraphs.forEach(p => {
          p.setAttribute('style', `margin: 0; color: ${config.textColor}; font-size: 15px; line-height: 1.6;`)
        })

        // Style lists
        const lists = block.querySelectorAll('ul, ol')
        lists.forEach(list => {
          list.setAttribute('style', `margin: 0; color: ${config.textColor}; font-size: 15px; line-height: 1.6; padding-left: 20px;`)
        })

        const listItems = block.querySelectorAll('li')
        listItems.forEach(li => {
          li.setAttribute('style', `color: ${config.textColor};`)
        })
      })

      const wrapper = doc.getElementById('wrapper')
      return wrapper ? wrapper.innerHTML : html
    }
  }
}

class AlertBlockUI extends Plugin {
  static get pluginName() {
    return 'AlertBlockUI'
  }

  init() {
    this.editor.ui.componentFactory.add('alertBlock', locale => this._createDropdown(locale))
  }

  _createDropdown(locale) {
    const editor = this.editor
    const button = new ButtonView(locale)

    button.set({
      label: locale.t('Alert Block'),
      icon: TOOLBAR_ICON,
      tooltip: true,
      withText: false
    })

    const dropdownPanel = document.createElement('div')
    dropdownPanel.className = 'ck-alert-block-dropdown'
    dropdownPanel.style.cssText = `display: none; position: absolute; top: 100%; left: 0; background: var(--ck-color-base-background, #fff); border: 1px solid var(--ck-color-base-border, #ccc); border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); z-index: 10000; min-width: 150px; padding: 4px 0;`

    for (const [type, config] of Object.entries(ALERT_TYPES)) {
      const item = document.createElement('button')
      item.type = 'button'
      const smallIcon = config.icon.replace('width="24"', 'width="18"').replace('height="24"', 'height="18"')
      item.innerHTML = `<span style="margin-right: 8px; display: inline-flex;">${smallIcon}</span><span>${config.label}</span>`
      item.style.cssText = `display: flex; align-items: center; width: 100%; padding: 8px 12px; border: none; background: none; cursor: pointer; font-size: 14px; color: var(--ck-color-base-text, #333);`
      item.onmouseenter = () => item.style.backgroundColor = 'var(--ck-color-list-button-hover-background, #f0f0f0)'
      item.onmouseleave = () => item.style.backgroundColor = 'transparent'
      item.onclick = (e) => {
        e.preventDefault()
        e.stopPropagation()
        editor.execute(`alertBlock:${type}`)
        dropdownPanel.style.display = 'none'
        editor.editing.view.focus()
      }
      dropdownPanel.appendChild(item)
    }

    let isOpen = false
    button.on('execute', () => {
      isOpen = !isOpen
      dropdownPanel.style.display = isOpen ? 'block' : 'none'
    })

    document.addEventListener('click', (e) => {
      if (isOpen && !button.element?.contains(e.target) && !dropdownPanel.contains(e.target)) {
        isOpen = false
        dropdownPanel.style.display = 'none'
      }
    })

    button.on('render', () => {
      if (button.element) {
        button.element.style.position = 'relative'
        button.element.appendChild(dropdownPanel)
      }
    })

    return button
  }
}

export default class AlertBlock extends Plugin {
  static get requires() {
    return [AlertBlockEditing, AlertBlockUI]
  }

  static get pluginName() {
    return 'AlertBlock'
  }
}

export { AlertBlock, AlertBlockEditing, AlertBlockUI, ALERT_TYPES }
